/**
 * As cinco posições da barra inferior e o mapeamento rota → aba.
 *
 * Decisão do PO (17/09/2026, lote de aparência 1): Home · Financeiro · "+" ·
 * Roteiro · Mais. O "+" não é rota — abre a folha de criações. Mapa e
 * Memórias saem da barra e vivem no Mais, com as demais ferramentas.
 *
 * A barra só aparece nas **quatro raízes** (`/`, `/financeiro`, `/roteiro`,
 * `/mais`); toda tela interna fica sem barra, com seta de voltar no
 * cabeçalho (`isRootRoute`, `rootOf`). `tabOf` continua puro e testado: é
 * ele que decide qual aba fica destacada e para que raiz a seta volta quando
 * não há histórico (notificação, link).
 */

export const TABS = ['home', 'financeiro', 'criar', 'roteiro', 'mais'] as const;

export type TabName = (typeof TABS)[number];

/** Abas que são rotas (o "+" não é). */
export type RouteTab = Exclude<TabName, 'criar'>;

export const TAB_LABELS: Record<TabName, string> = {
  home: 'Home',
  financeiro: 'Financeiro',
  criar: 'Criar',
  roteiro: 'Roteiro',
  mais: 'Mais',
};

export const TAB_ROUTES: Record<RouteTab, string> = {
  home: '/',
  financeiro: '/financeiro',
  roteiro: '/roteiro',
  mais: '/mais',
};

/**
 * Rotas que pertencem a cada aba mesmo estando fora dela na árvore de arquivos.
 * Só as exceções entram aqui; o caso comum é o prefixo da própria rota.
 */
const ROUTE_TO_TAB: Record<string, RouteTab> = {
  // Roteiro
  dia: 'roteiro',
  atividade: 'roteiro',
  'editar-atividade': 'roteiro',
  historico: 'roteiro',
  'nova-atividade': 'roteiro',
  // Financeiro
  'nova-despesa': 'financeiro',
  despesa: 'financeiro',
  fechar: 'financeiro',
  // Mais — todas as ferramentas e a conta
  mapa: 'mais',
  lugares: 'mais',
  lugar: 'mais',
  localizacao: 'mais',
  memorias: 'mais',
  album: 'mais',
  foto: 'mais',
  video: 'mais',
  upload: 'mais',
  bagagem: 'mais',
  enquetes: 'mais',
  ranking: 'mais',
  passaporte: 'mais',
  carro: 'mais',
  emergencia: 'mais',
  compras: 'mais',
  'item-compra': 'mais',
  documentos: 'mais',
  grupo: 'mais',
  hospedagem: 'mais',
  'config-viagem': 'mais',
  viagem: 'mais',
  viagens: 'mais',
  perfil: 'mais',
  negado: 'mais',
  sugestoes: 'mais',
};

function primeiroSegmento(route: string): string {
  return route.replace(/^\/+/, '').split('/')[0] ?? '';
}

/**
 * Aba destacada para uma rota. Devolve `null` para telas fora da sessão
 * (splash, login, convite, criar-viagem), que não exibem a barra.
 */
export function tabOf(route: string): RouteTab | null {
  const normalized = primeiroSegmento(route);
  if (normalized === '') return 'home';

  if (normalized in TAB_ROUTES) return normalized as RouteTab;

  return ROUTE_TO_TAB[normalized] ?? null;
}

/** As quatro telas em que a barra inferior existe. */
export function isRootRoute(route: string): boolean {
  const semBarra = route.replace(/\/+$/, '') || '/';
  return semBarra === '/' || Object.values(TAB_ROUTES).includes(semBarra);
}

/**
 * Para onde a seta de voltar leva quando não há tela anterior: a raiz da aba
 * a que a rota pertence. Fora da sessão, a Home.
 */
export function rootOf(route: string): string {
  const tab = tabOf(route);
  return tab ? TAB_ROUTES[tab] : '/';
}

/** A barra inferior só aparece nas raízes. */
export function showsBottomNav(route: string): boolean {
  return tabOf(route) !== null && isRootRoute(route);
}
